// Pointer-based drag system for moving cards between Hand → Plank,
// reordering within the Plank, and removing from the Plank back to Hand.
// Also detects long-press (no-move hold) to trigger an enlarged preview.
//
// Works on desktop (mouse) and mobile (touch) via Pointer Events.
const LONG_PRESS_MS = 380;
const DRAG_THRESHOLD_PX = 8;
let active = null;
export function attachDragHandlers(root, handlers) {
    // Attach to hand and plank areas; uses event delegation since cards
    // re-render frequently.
    root.querySelectorAll('.hand .pcard, .plank .pcard').forEach((cardEl) => {
        cardEl.addEventListener('pointerdown', (e) => onPointerDown(e, cardEl, handlers, root));
    });
}
function onPointerDown(e, cardEl, handlers, root) {
    if (e.button !== 0 && e.pointerType === 'mouse')
        return;
    if (active)
        return;
    e.preventDefault();
    // Lock pointer to the card so subsequent move/up events don't get hijacked
    // by scroll containers. Combined with touch-action: none on .pcard, this
    // is what makes vertical drags actually work on mobile.
    try {
        cardEl.setPointerCapture(e.pointerId);
    }
    catch { /* ignore */ }
    const cardId = cardEl.dataset.cardId;
    const fromPlank = !!cardEl.closest('.plank');
    active = {
        pointerId: e.pointerId,
        cardId,
        fromPlank,
        ghost: null,
        startX: e.clientX,
        startY: e.clientY,
        longPressTimer: null,
        hasMoved: false,
        sourceEl: cardEl,
    };
    // Long-press fires if no significant movement
    active.longPressTimer = window.setTimeout(() => {
        if (active && !active.hasMoved) {
            handlers.onLongPress(cardId, cardEl);
            // Cancel the drag — long-press takes precedence
            cleanup();
        }
    }, LONG_PRESS_MS);
    const onMove = (ev) => {
        if (!active || ev.pointerId !== active.pointerId)
            return;
        const dx = ev.clientX - active.startX;
        const dy = ev.clientY - active.startY;
        if (!active.hasMoved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
            // Begin actual drag
            active.hasMoved = true;
            if (active.longPressTimer !== null) {
                clearTimeout(active.longPressTimer);
                active.longPressTimer = null;
            }
            startGhost(cardEl, ev);
        }
        if (active.hasMoved && active.ghost) {
            active.ghost.style.left = `${ev.clientX}px`;
            active.ghost.style.top = `${ev.clientY}px`;
            updateDropIndicators(ev, root);
        }
    };
    const onUp = (ev) => {
        if (!active || ev.pointerId !== active.pointerId)
            return;
        if (active.longPressTimer !== null)
            clearTimeout(active.longPressTimer);
        if (!active.hasMoved) {
            // Tap behavior — toggle selection / remove from plank
            handlers.onTap(active.cardId, active.fromPlank);
            cleanup();
            return;
        }
        // Drag end: figure out drop target
        const drop = resolveDropTarget(ev, root);
        if (drop.kind === 'plank') {
            if (active.fromPlank) {
                handlers.onReorderPlank(active.cardId, drop.index);
            }
            else {
                handlers.onDropToPlank(active.cardId, drop.index);
            }
        }
        else if (drop.kind === 'hand' && active.fromPlank) {
            handlers.onRemoveFromPlank(active.cardId);
        }
        else if (drop.kind === 'trash' && handlers.onDropToTrash) {
            handlers.onDropToTrash(active.cardId, active.fromPlank);
        }
        cleanup();
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    function cleanup() {
        if (active?.ghost)
            active.ghost.remove();
        cardEl.classList.remove('pcard--dragging');
        clearDropIndicators(root);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        active = null;
    }
}
function startGhost(sourceEl, ev) {
    if (!active)
        return;
    sourceEl.classList.add('pcard--dragging');
    const ghost = sourceEl.cloneNode(true);
    ghost.classList.add('pcard--ghost');
    ghost.classList.remove('pcard--selected', 'pcard--dragging');
    ghost.style.width = `${sourceEl.offsetWidth}px`;
    ghost.style.height = `${sourceEl.offsetHeight}px`;
    ghost.style.left = `${ev.clientX}px`;
    ghost.style.top = `${ev.clientY}px`;
    ghost.style.transform = 'translate(-50%, -50%) scale(1.1) rotate(2deg)';
    document.body.appendChild(ghost);
    active.ghost = ghost;
}
function resolveDropTarget(ev, root) {
    const trash = root.querySelector('[data-trash-zone]');
    if (trash && rectContains(trash.getBoundingClientRect(), ev.clientX, ev.clientY)) {
        return { kind: 'trash' };
    }
    const plank = root.querySelector('.plank');
    const hand = root.querySelector('.hand-area');
    if (plank && rectContains(plank.getBoundingClientRect(), ev.clientX, ev.clientY)) {
        return { kind: 'plank', index: computePlankInsertIndex(plank, ev.clientX) };
    }
    if (hand && rectContains(hand.getBoundingClientRect(), ev.clientX, ev.clientY)) {
        return { kind: 'hand' };
    }
    return { kind: 'none' };
}
function computePlankInsertIndex(plank, x) {
    const cards = Array.from(plank.querySelectorAll('.pcard:not(.pcard--ghost):not(.pcard--dragging)'));
    for (let i = 0; i < cards.length; i++) {
        const r = cards[i].getBoundingClientRect();
        if (x < r.left + r.width / 2)
            return i;
    }
    return cards.length;
}
function rectContains(r, x, y) {
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}
function updateDropIndicators(ev, root) {
    clearDropIndicators(root);
    const drop = resolveDropTarget(ev, root);
    const plank = root.querySelector('.plank');
    const hand = root.querySelector('.hand-area');
    const trash = root.querySelector('[data-trash-zone]');
    if (drop.kind === 'plank' && plank) {
        plank.classList.add('plank--drop-active');
        const cards = Array.from(plank.querySelectorAll('.pcard:not(.pcard--ghost):not(.pcard--dragging)'));
        const marker = document.createElement('div');
        marker.className = 'drop-marker';
        if (drop.index >= cards.length) {
            plank.appendChild(marker);
        }
        else {
            plank.insertBefore(marker, cards[drop.index]);
        }
    }
    else if (drop.kind === 'hand' && hand) {
        hand.classList.add('hand-area--drop-active');
    }
    else if (drop.kind === 'trash' && trash) {
        trash.classList.add('trash-bin--drop-active');
    }
}
function clearDropIndicators(root) {
    root.querySelectorAll('.plank--drop-active').forEach((el) => el.classList.remove('plank--drop-active'));
    root.querySelectorAll('.hand-area--drop-active').forEach((el) => el.classList.remove('hand-area--drop-active'));
    root.querySelectorAll('.trash-bin--drop-active').forEach((el) => el.classList.remove('trash-bin--drop-active'));
    root.querySelectorAll('.drop-marker').forEach((el) => el.remove());
}
// Generic "click & hold to enlarge" — for shop offers, crew tiles, etc.
// Shorter long-press threshold than drag because there's no drag intent.
export function attachLongPressPreview(el, buildHtml, onTap) {
    let timer = null;
    let moved = false;
    let startX = 0, startY = 0;
    let triggered = false;
    const cleanup = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
    };
    const onMove = (e) => {
        if (Math.hypot(e.clientX - startX, e.clientY - startY) > 8) {
            moved = true;
            cleanup();
        }
    };
    const onUp = () => {
        cleanup();
        if (!triggered && !moved && onTap)
            onTap();
    };
    el.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 && e.pointerType === 'mouse')
            return;
        triggered = false;
        moved = false;
        startX = e.clientX;
        startY = e.clientY;
        timer = window.setTimeout(() => {
            if (!moved) {
                triggered = true;
                showCardPreview(buildHtml());
            }
        }, 350);
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
    });
}
// ─── Long-press preview (large card overlay) ─────────────────────
export function showCardPreview(html) {
    closeCardPreview();
    const overlay = document.createElement('div');
    overlay.id = 'card-preview-overlay';
    overlay.className = 'card-preview-overlay';
    overlay.innerHTML = `<div class="card-preview-content">${html}</div>`;
    document.body.appendChild(overlay);
    void overlay.offsetWidth;
    overlay.classList.add('card-preview-overlay--in');
    const close = () => closeCardPreview();
    overlay.addEventListener('pointerdown', close);
    overlay.addEventListener('pointerup', close);
    setTimeout(() => {
        document.addEventListener('pointerdown', close, { once: true });
    }, 100);
}
export function closeCardPreview() {
    document.getElementById('card-preview-overlay')?.remove();
}
